import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type WxCopilotContextType = {
  isPanelOpen: boolean;
  setIsPanelOpen: (isPanelOpen: boolean) => void;
  isCopilotRendered: boolean;
  setIsCopilotRendered: (isCopilotRendered: boolean) => void;
  /** Last copilot tool name recorded for mutation follow-up (React state only). */
  lastToolCallName: string | null;
  recordLastToolCallName: (toolName: string) => void;
  clearLastToolCallName: () => void;
};

const WxCopilotContext = createContext<WxCopilotContextType | undefined>(
  undefined,
);

export function WxCopilotProvider({ children }: { children: React.ReactNode }) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCopilotRendered, setIsCopilotRendered] = useState(false);
  const [lastToolCallName, setLastToolCallName] = useState<string | null>(null);

  const recordLastToolCallName = useCallback((toolName: string) => {
    setLastToolCallName(toolName);
  }, []);

  const clearLastToolCallName = useCallback(() => {
    setLastToolCallName(null);
  }, []);

  const value = useMemo(
    () => ({
      isPanelOpen,
      setIsPanelOpen,
      isCopilotRendered,
      setIsCopilotRendered,
      lastToolCallName,
      recordLastToolCallName,
      clearLastToolCallName,
    }),
    [
      isPanelOpen,
      setIsPanelOpen,
      isCopilotRendered,
      setIsCopilotRendered,
      lastToolCallName,
      recordLastToolCallName,
      clearLastToolCallName,
    ],
  );

  return (
    <WxCopilotContext.Provider value={value}>
      {children}
    </WxCopilotContext.Provider>
  );
}

export function useWxCopilotContext() {
  const context = useContext(WxCopilotContext);
  if (!context) {
    throw new Error(
      'useWxCopilotContext must be used within a WxCopilotProvider',
    );
  }
  return context;
}
